import { autoinject } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { NetsServiceDashboardService } from 'services/statistics-dashboard-service';
import { FilterManager } from 'lib/tables/FilterManager';
import { Filters, filterTypes } from 'elements/Filter';
import { I18N } from 'aurelia-i18n';
import { TabManager } from 'views/admin/mooring/tab-manager';
import { PubSub } from 'lib/event/PubSub';
import { DataFilter } from 'lib/tables/DataFilter';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';
import { Models } from 'models/core';
import { AsyncErrorHandler } from 'lib/ui';
import { QueryFilter } from 'lib/tables/DataFilter';
import { NetDamageDataByLocation, NetsDamagedByCustomerDTO, NetsServiceDashboard } from 'models/statistics/nets';
import { StatisticsDashboardCommonService } from 'services/statistics-dashboard-service';
import { StatisticsExportDialog, ExportDialogModel } from 'elements/statistics-export-dialog';
import { TableData, ChartData } from 'services/statistics-export-service';

type TableFilter = {
  dateOffsetFrom?: string;
  dateOffsetTo?: string;
} & DataFilter;

@autoinject()
export class TabNetsService {
  protected dataFilter: TableFilter = new DataFilter();
  protected fetchingData: boolean = false;
  protected queryFilter: QueryFilter = null;

  protected data: NetsServiceDashboard = null;
  protected customers: Models.Customer[] = [];
  protected sites: Models.Site[] = [];

  protected averageNetsDamageSectionTitle: string = `${this.t.tr('general.average')} ${this.t.tr(
    'reports.service.servicecard.repairplacement'
  )}`;
  protected sectionTitle: string = `${this.t.tr('general.summary')}`;

  constructor(
    private netsServiceDashboardService: NetsServiceDashboardService,
    private pubsub: PubSub,
    private t: I18N,
    protected fm: FilterManager,
    private customerService: CustomerService,
    private siteService: SiteService,
    tabManager: TabManager,
    private statisticsDashboardCommonService: StatisticsDashboardCommonService,
    private dialogService: DialogService
  ) {
    tabManager.tabActivated();
    this.pubsub.sub('filter-manager:filter-changed', () => {
      void this.getNetsServiceData();
    });
    this.pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
  }

  private onLocaleChanged() {
    this.sectionTitle = `${this.t.tr('general.summary')}`;
    this.averageNetsDamageSectionTitle = `${this.t.tr('general.average')} ${this.t.tr('reports.service.servicecard.repairplacement')}`;
    void this.getNetsServiceData();
  }

  @AsyncErrorHandler
  private async load() {
    this.customers = await this.customerService.getAll();
    const sites = await this.siteService.getAll();
    this.sites = sites.filter((x) => !x.IsDeleted);
  }

  async bind() {
    await this.load();
    const now = new Date();
    const year = now.getFullYear();
    const firstDay = new Date(year, 0, 1, 0, 0, 0, 0);
    const lastDay = new Date(year, 11, 31, 23, 59, 59, 999);

    this.statisticsDashboardCommonService.updateDatesInLocalStorage(firstDay, lastDay, 'statistics_nets_service');

    this.fm.init({
      context: 'statistics_nets_service',
      storageKey: 'statistics_nets_service',
      filterFilters: (filters) => {
        return filters;
      },
      filters: {
        customerIds: {
          name: 'customerIds',
          label: this.t.tr('general.customer'),
          type: filterTypes.CHECKBOX,
          options: this.customers,
          query: 'customerIds',
        },
        siteIds: {
          name: 'siteIds',
          label: this.t.tr('general.locality'),
          type: filterTypes.CHECKBOX,
          options: this.sites.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'siteIds',
        },
        dateOfGoodsReceipt: {
          name: 'dateOffset',
          label: this.t.tr('general.date'),
          type: filterTypes.RANGE_DATE,
          values: { from: new Date(), to: new Date() },
          defaultValues: { from: firstDay, to: lastDay },
        },
      } satisfies Filters,
      defaultFilters: {
        customerIds: true,
        siteIds: true,
        dateOfGoodsReceipt: true,
      },
    });

    this.queryFilter = this.fm.queryFilter;
  }

  detached() {
    this.pubsub.unsub();
  }

  protected summaryKpis: {
    title: string;
    values:
      | {
          nets: { value: number | string; decimals?: number } | undefined;
          specialProducts: { value: number | string; decimals?: number } | undefined;
        }
      | undefined;
  }[] = [];
  protected damageByLocationData: NetDamageDataByLocation[] = [];
  protected topCustomerDamageData: NetsDamagedByCustomerDTO[] = [];
  protected overallDamageAverage: {
    Repairs1To3Msk: number;
    Repairs4To10Msk: number;
    RepairsOver10Msk: number;
    BrokenLaces: number;
  } | null = null;

  @AsyncErrorHandler
  protected async getNetsServiceData() {
    this.queryFilter = this.fm.queryFilter;
    this.fetchingData = true;

    const { data: serviceData } = await this.netsServiceDashboardService.getNetsServiceData(this.fm.queryFilter, false);
    if (serviceData) {
      this.data = serviceData;

      this.summaryKpis = [
        {
          title: `${this.t.tr('general.Received')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsReceived ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsReceived ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.Delivered')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsDelivered ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsDelivered ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.inStorage')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsUnderService ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsUnderService ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.Scrapped')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsScrapped ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsScrapped ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.washed')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsWashed ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsWashed ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.repaired')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsRepaired ?? 0 },
            specialProducts: { value: serviceData?.TotalNumberOfSpecialProductsRepaired ?? 0 },
          },
        },
        {
          title: `${this.t.tr('general.extendedLifeSpan')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsWithExtendedLifespan ?? 0 },
            specialProducts: undefined,
          },
        },
        {
          title: `${this.t.tr('general.antifouled')}`,
          values: {
            nets: { value: serviceData?.TotalNumberOfNetsAntifouled ?? 0 },
            specialProducts: undefined,
          },
        },
        {
          title: `${this.t.tr('general.repaired')}`,
          values: {
            nets: { value: serviceData?.TotalHoursUsedForRepairing ?? 0, decimals: 2 },
            specialProducts: { value: serviceData?.TotalHoursUsedForRepairingSpecialProduct ?? 0, decimals: 2 },
          },
        },
        {
          title: `${this.t.tr('general.amountOfImpregnation')}`,
          values: {
            nets: { value: serviceData?.TotalImpregnationLiters ?? 0, decimals: 2 },
            specialProducts: undefined,
          },
        },
      ];
    }

    const { data: repairData } = await this.netsServiceDashboardService.getAverageNetsDamageData(this.fm.queryFilter, false);
    if (repairData) {
      this.damageByLocationData = repairData?.AverageNetsDamageByLocation ?? [];
      this.overallDamageAverage = {
        Repairs1To3Msk: repairData?.AverageNetsDamageOneToThree ?? 0,
        Repairs4To10Msk: repairData?.AverageNetsDamageFourToTen ?? 0,
        RepairsOver10Msk: repairData?.AverageNetsDamageMoreThanTen ?? 0,
        BrokenLaces: repairData?.AverageNetsDamageBrokenLaces ?? 0,
      };
    }

    this.fetchingData = false;
  }

  protected async openExportDialog(): Promise<void> {
    const { asQueryString, ...filterProps } = this.fm.queryFilter;
    const topCustomersQuery = new DataFilter({
      ...filterProps,
      top: 10,
    });
    const { data: customerDamageData } = await this.netsServiceDashboardService.getNetsDamageDataPerCustomer(
      topCustomersQuery,
      false,
      Models.RepairType.Unknown,
      true
    );
    if (customerDamageData) {
      this.topCustomerDamageData = customerDamageData?.YearlyNetsDamageReportByCustomer ?? [];
    }

    const tables = this.buildExportTables();
    const charts = this.buildExportCharts();
    const dateRange = this.getDateRange();

    const model: ExportDialogModel = {
      tables,
      charts,
      dateRange,
      reportTitle: this.t.tr('reports.servicestatistics.title'),
    };

    this.dialogService.open({
      viewModel: StatisticsExportDialog,
      model,
      lock: false,
    });
  }

  private buildExportTables(): TableData[] {
    const tables: TableData[] = [];

    // 1. Summary table
    const summaryTable: TableData = {
      title: this.sectionTitle,
      headers: ['', this.t.tr('notlogg.net_plural'), this.t.tr('general.specialproducts')],
      rows: this.summaryKpis.map((kpi) => [
        kpi.title,
        kpi.values?.nets?.value ?? '--',
        kpi.values?.specialProducts?.value ?? '--',
      ]),
    };
    
    if (summaryTable.rows.length > 0) {
      tables.push(summaryTable);
    }

    // 2. Damage by location table
    if (this.damageByLocationData.length > 0) {
      const damageRows = this.damageByLocationData.map((item) => [
        item.LocationName,
        item.Repairs1To3Msk?.toFixed(2) ?? '--',
        item.Repairs4To10Msk?.toFixed(2) ?? '--',
        item.RepairsOver10Msk?.toFixed(2) ?? '--',
        item.BrokenLaces?.toFixed(2) ?? '--',
      ]);

      if (this.overallDamageAverage) {
        damageRows.push([
          this.t.tr('general.average'),
          this.overallDamageAverage.Repairs1To3Msk?.toFixed(2) ?? '--',
          this.overallDamageAverage.Repairs4To10Msk?.toFixed(2) ?? '--',
          this.overallDamageAverage.RepairsOver10Msk?.toFixed(2) ?? '--',
          this.overallDamageAverage.BrokenLaces?.toFixed(2) ?? '--',
        ]);
      }

      const damageTable: TableData = {
        title: this.averageNetsDamageSectionTitle,
        headers: [
          this.t.tr('reports.service.servicecard.repairplacement'),
          '1-3 msk',
          '4-10 msk',
          '>10 msk',
          'Ant. brudd løkker',
        ],
        rows: damageRows,
      };
      tables.push(damageTable);
    }

    // 3. Repairing data table
    const repairingData = this.data?.YearlyNetsRepairingData || [];
    if (repairingData.length > 0) {
      const repairingRows: (string | number)[][] = [];
      const sortedYears = [...repairingData].sort((a, b) => (b.Year ?? 0) - (a.Year ?? 0));

      for (const yearData of sortedYears) {
        if (!yearData?.RepairingData) continue;
        
        // Year header row
        repairingRows.push([`Year: ${yearData.Year}`, '', '', '', '', '', '']);
        
        const nets = [...yearData.RepairingData].sort((a, b) => (a.NetType ?? '').localeCompare(b.NetType ?? ''));
        for (const net of nets) {
          const rds = net.RepairingData || [];
          if (rds.length === 0) {
            repairingRows.push([net.NetType ?? '', '', '', '', '', '', '']);
          } else {
            const r = rds[0];
            const count = Number(r.Count);
            const hours = Number(r.Hours);
            const avg = r.AverageHours ?? (Number.isFinite(count) && count > 0 && Number.isFinite(hours) ? hours / count : 0);
            const d = r.DamageData;
            repairingRows.push([
              net.NetType ?? '',
              Number.isFinite(hours) ? hours : '--',
              Number.isFinite(count) ? count : '--',
              Number.isFinite(avg) ? avg.toFixed(2) : '--',
              Number.isFinite(count) && count > 0 && d ? (d.Repairs1To3Msk?.toFixed(2) ?? '--') : '--',
              Number.isFinite(count) && count > 0 && d ? (d.Repairs4To10Msk?.toFixed(2) ?? '--') : '--',
              Number.isFinite(count) && count > 0 && d ? (d.RepairsOver10Msk?.toFixed(2) ?? '--') : '--',
            ]);
          }
        }
      }

      if (repairingRows.length > 0) {
        tables.push({
          title: this.t.tr('service.repairTab'),
          headers: [
            this.t.tr('general.NetType'),
            'Hours',
            'Count',
            'Average Hours',
            'Repairs 1-3 Msk',
            'Repairs 4-10 Msk',
            'Repairs >10 Msk',
          ],
          rows: repairingRows,
        });
      }
    }

    // 4. Impregnation data table
    const impregnationData = this.data?.YearlyNetsImpregnationData || [];
    if (impregnationData.length > 0) {
      const impregnationRows: (string | number)[][] = [];
      const sortedYears = [...impregnationData].sort((a, b) => (b.Year ?? 0) - (a.Year ?? 0));

      for (const yearData of sortedYears) {
        if (!yearData?.YearlyImpregnationData) continue;
        
        // Year header row
        impregnationRows.push([`Year: ${yearData.Year}`, '', '', '']);
        
        const nets = [...yearData.YearlyImpregnationData].sort((a, b) => 
          (a.NetType ?? '').localeCompare(b.NetType ?? '')
        );
        
        for (const net of nets) {
          const idata = net.ImpregnationData || [];
          for (const imp of idata) {
            impregnationRows.push([
              net.NetType ?? '',
              imp.ImpregnationType ?? '',
              imp.Count ?? '--',
              imp.AverageAmount?.toFixed(2) ?? '--',
            ]);
          }
        }
      }

      if (impregnationRows.length > 0) {
        tables.push({
          title: this.t.tr('general.antifouling'),
          headers: [
            this.t.tr('general.NetType'),
            'Impregnation Type',
            'Count',
            'Avg Liters / Service',
          ],
          rows: impregnationRows,
        });
      }
    }

    return tables;
  }

  private buildExportCharts(): ChartData[] {
    const charts: ChartData[] = [];

    const netsPlural = this.t.tr('general.nets');
    const quarter = this.t.tr('general.quarter');
    const specialProducts = this.t.tr('general.specialproducts');

    // Nets per Quarter chart
    const netsServiceData = this.data?.YearlyNetsServiceData || [];
    if (netsServiceData.length > 0) {
      const netsChart = this.buildQuarterlyChart(
        netsServiceData,
        `${netsPlural} Per ${quarter}`
      );
      if (netsChart) charts.push(netsChart);
    }

    // Special Products per Quarter chart
    const specialProductsData = this.data?.YearlySpecialProductsServiceData || [];
    if (specialProductsData.length > 0) {
      const specialChart = this.buildQuarterlyChart(
        specialProductsData,
        `${specialProducts} Per ${quarter}`
      );
      if (specialChart) charts.push(specialChart);
    }

    // Customers with most damages chart
    if (this.topCustomerDamageData && this.topCustomerDamageData.length > 0) {
      const customerChart = this.buildCustomerDamageChart(
        this.topCustomerDamageData,
        this.t.tr('customer.withMostDamage') || 'Kunder med flest skader'
      );
      if (customerChart) charts.push(customerChart);
    }

    return charts;
  }

  private buildCustomerDamageChart(data: NetsDamagedByCustomerDTO[], title: string): ChartData | null {
    if (!data || data.length === 0) return null;

    // Get all years from the data
    const years = Array.from(new Set(data.flatMap((item) => item.YearlyDamage?.map((d) => d.Year) || []))).sort();
    
    // Categories are years
    const categories = years.map(String);

    // Each customer is a series
    const series = data.map((customer) => {
      const values = years.map((year) => {
        const yearData = customer.YearlyDamage?.find((d) => d.Year === year);
        return yearData?.DamageCount ?? 0;
      });

      return {
        name: customer.CustomerName || 'Unknown',
        values,
      };
    });

    return {
      title,
      categories,
      series,
    };
  }

  private buildQuarterlyChart(
    data: { ServiceStatus: string; ServiceStatusCounts: { Year: number; QuarterlyData: { Quarter: string; Count: number }[] }[] }[],
    title: string
  ): ChartData | null {
    if (!data || data.length === 0) return null;

    // Build categories (Year-Quarter combinations)
    const yearToQuarters = new Map<number, Set<string>>();
    for (const item of data) {
      for (const entry of item.ServiceStatusCounts || []) {
        const year = entry.Year;
        for (const qRec of entry.QuarterlyData || []) {
          let quarterSet = yearToQuarters.get(year);
          if (!quarterSet) {
            quarterSet = new Set();
            yearToQuarters.set(year, quarterSet);
          }
          quarterSet.add(qRec.Quarter);
        }
      }
    }

    const sortedYears = Array.from(yearToQuarters.keys()).sort((a, b) => a - b);
    const categories: string[] = [];
    const leafOrder: { year: number; quarter: string }[] = [];

    for (const year of sortedYears) {
      const quarters = Array.from(yearToQuarters.get(year) || []).sort();
      for (const q of quarters) {
        categories.push(`${year} ${q}`);
        leafOrder.push({ year, quarter: q });
      }
    }

    // Build series
    const series = data.map((item) => {
      const values = leafOrder.map(({ year, quarter }) => {
        const yearData = item.ServiceStatusCounts?.find((y) => y.Year === year);
        const quarterData = yearData?.QuarterlyData?.find((q) => q.Quarter === quarter);
        return quarterData?.Count ?? 0;
      });

      return {
        name: item.ServiceStatus,
        values,
      };
    });

    return {
      title,
      categories,
      series,
    };
  }

  private getDateRange(): { from: Date; to: Date } | undefined {
    const dateFilter = (this.fm.filters as Record<string, { values?: { from?: Date; to?: Date } }>)?.dateOfGoodsReceipt;
    if (dateFilter?.values?.from && dateFilter?.values?.to) {
      return {
        from: new Date(dateFilter.values.from),
        to: new Date(dateFilter.values.to),
      };
    }
    return undefined;
  }
}
